import React from "react";
import { motion } from "framer-motion";
import {
  Phone, Mail, MapPin, Clock, Linkedin, Facebook, MessageCircle,
} from "lucide-react";
import {
  CONTACT, WHATSAPP_URL, TEL_URL, MAIL_URL,
} from "../../lib/contact";

export default function Contact() {
  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-brand-light py-24 lg:py-32 overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 bg-blueprint-grid bg-grid-40 opacity-40" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-brand-gold" />
            <span className="label-overline">Contacto Directo</span>
          </div>
          <h2 className="mt-5 font-playfair text-3xl sm:text-4xl lg:text-5xl text-brand-navy leading-[1.1] tracking-tight text-balance">
            Hablemos de <span className="italic text-brand-gold">tu proyecto</span>
          </h2>
          <p className="mt-5 font-inter text-lg text-brand-mute leading-relaxed max-w-xl">
            Atención personal con el Arq. Angel Rodríguez Juárez. Cuéntanos tu caso por el
            canal que prefieras y recibirás una respuesta clara, rápida y profesional.
          </p>

          <ul className="mt-10 space-y-4 max-w-xl">
            <ContactRow
              icon={MessageCircle}
              label="WhatsApp"
              value={CONTACT.phoneIntl}
              href={WHATSAPP_URL}
              external
              testId="contact-whatsapp"
              accent
            />
            <ContactRow
              icon={Phone}
              label="Teléfono"
              value={CONTACT.phoneIntl}
              href={TEL_URL}
              testId="contact-phone"
            />
            <ContactRow
              icon={Mail}
              label="Correo electrónico"
              value={CONTACT.email}
              href={MAIL_URL}
              testId="contact-email"
            />
            <ContactRow
              icon={MapPin}
              label="Ubicación"
              value={
                <span>
                  {CONTACT.address}
                  <br />
                  {CONTACT.city}
                </span>
              }
              testId="contact-location"
            />
            <ContactRow
              icon={Clock}
              label="Horario de atención"
              value={
                <span>
                  {CONTACT.hoursWeek}
                  <br />
                  {CONTACT.hoursSat}
                </span>
              }
              testId="contact-hours"
            />
          </ul>

          <div className="mt-10 flex items-center gap-3">
            <span className="font-mont text-[10px] uppercase tracking-[0.22em] text-brand-mute">
              Síguenos
            </span>
            <span className="h-px w-8 bg-brand-gold/60" />
            <a
              href="https://www.linkedin.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              data-testid="social-linkedin"
              className="flex h-9 w-9 items-center justify-center rounded-sm border border-brand-navy/15 text-brand-navy hover:bg-brand-navy hover:text-brand-gold transition-colors"
            >
              <Linkedin size={16} />
            </a>
            <a
              href="https://www.facebook.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="social-facebook"
              className="flex h-9 w-9 items-center justify-center rounded-sm border border-brand-navy/15 text-brand-navy hover:bg-brand-navy hover:text-brand-gold transition-colors"
            >
              <Facebook size={16} />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              data-testid="social-whatsapp"
              className="flex h-9 w-9 items-center justify-center rounded-sm border border-brand-navy/15 text-brand-navy hover:bg-brand-navy hover:text-brand-gold transition-colors"
            >
              <MessageCircle size={16} />
            </a>
          </div>
        </div>

        {/* RIGHT side — visual contact card (no form) */}
        <motion.aside
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-5"
        >
          <div className="relative bg-brand-navy text-white rounded-sm overflow-hidden border border-brand-gold/40 shadow-[0_30px_60px_-30px_rgba(27,42,74,0.4)]">
            <div aria-hidden className="absolute inset-0 bg-blueprint-dark bg-grid-32 opacity-50" />
            <div className="relative p-8 lg:p-10">
              <span className="font-mont text-[10px] uppercase tracking-[0.28em] text-brand-gold">
                Atención Inmediata
              </span>
              <h3 className="mt-3 font-playfair text-2xl lg:text-3xl text-white leading-tight">
                Respuesta en menos de <span className="text-brand-gold">24 horas</span>
              </h3>
              <div className="my-8 gold-divider" />

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-card-whatsapp"
                className="group flex items-center justify-between rounded-sm bg-brand-gold text-brand-navy px-5 py-4 hover:bg-white transition-colors"
              >
                <div className="flex items-center gap-3">
                  <MessageCircle size={20} />
                  <div>
                    <div className="font-mont text-[10px] uppercase tracking-[0.22em] opacity-80">
                      WhatsApp
                    </div>
                    <div className="font-playfair text-lg">{CONTACT.phoneIntl}</div>
                  </div>
                </div>
                <span className="font-mont text-[10px] uppercase tracking-[0.18em] opacity-70 group-hover:opacity-100">
                  Escribir →
                </span>
              </a>

              <a
                href={MAIL_URL}
                data-testid="contact-card-email"
                className="mt-3 group flex items-center justify-between rounded-sm border border-white/20 px-5 py-4 hover:border-brand-gold hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Mail size={20} className="text-brand-gold" />
                  <div>
                    <div className="font-mont text-[10px] uppercase tracking-[0.22em] text-white/60">
                      Correo electrónico
                    </div>
                    <div className="font-inter text-sm text-white">{CONTACT.email}</div>
                  </div>
                </div>
                <span className="font-mont text-[10px] uppercase tracking-[0.18em] text-white/60 group-hover:text-brand-gold">
                  Enviar →
                </span>
              </a>

              <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-gold/15 border border-brand-gold/30">
                  <Clock size={18} className="text-brand-gold" />
                </div>
                <div>
                  <div className="font-mont text-[10px] uppercase tracking-[0.22em] text-white/60">
                    Horario
                  </div>
                  <div className="font-inter text-sm text-white">
                    Lun–Vie 9:00–18:00 · Sáb 9:00–13:00
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.aside>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, href, external, testId, accent }) {
  const content = (
    <div className="flex items-start gap-4 group">
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-sm border ${
          accent
            ? "bg-brand-gold/10 border-brand-gold/40 text-brand-gold-dark"
            : "border-brand-navy/15 text-brand-navy"
        }`}
      >
        <Icon size={18} strokeWidth={1.6} />
      </div>
      <div className="pt-1">
        <div className="font-mont text-[10px] uppercase tracking-[0.22em] text-brand-mute">
          {label}
        </div>
        <div className="font-playfair text-lg text-brand-navy mt-0.5 group-hover:text-brand-gold-dark transition-colors">
          {value}
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <li>
        <a
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
          data-testid={testId}
        >
          {content}
        </a>
      </li>
    );
  }
  return <li data-testid={testId}>{content}</li>;
}
